import React, { useState, useEffect } from 'react';
import { X, User, Palette, Save, Trash2, Download, Upload } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { AppSettings, SessionSettings } from '../types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { state, dispatch } = useApp();
  const [activeTab, setActiveTab] = useState<'general' | 'session' | 'data' | 'profile'>('general');
  const [tempSettings, setTempSettings] = useState<AppSettings>(state.settings);
  const [tempSessionSettings, setTempSessionSettings] = useState<SessionSettings>(
    state.currentSession?.settings || {
      autoClearOnJoin: false,
      autoClearOnReload: false,
      allowAnonymousUsers: true,
      requireApproval: false,
      maxHistoryItems: 50,
      maxChatMessages: 100,
    }
  );

  // Update session settings when current session changes
  useEffect(() => {
    if (state.currentSession?.settings) {
      setTempSessionSettings(state.currentSession.settings);
    }
  }, [state.currentSession?.settings]);

  const [userName, setUserName] = useState(state.currentUser.name || '');
  const [userColor, setUserColor] = useState(state.currentUser.color || '#3b82f6');
  const [userAvatar, setUserAvatar] = useState(state.currentUser.avatar || '👤');

  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
  ];

  const avatars = ['👤', '👨', '👩', '🧑', '👨‍💻', '👩‍💻', '🎓', '👨‍🎓', '👩‍🎓', '🤓', '😎', '🧙‍♂️'];

  const handleSaveSettings = () => {
    dispatch({ type: 'UPDATE_APP_SETTINGS', payload: tempSettings });
    if (state.currentSession) {
      dispatch({ type: 'UPDATE_SESSION_SETTINGS', payload: tempSessionSettings });
    }
    onClose();
  };

  const handleSaveProfile = () => {
    const userId = state.currentUser.id || `user_${Date.now()}`;
    dispatch({
      type: 'SET_CURRENT_USER',
      payload: {
        id: userId,
        name: userName,
        color: userColor,
        avatar: userAvatar,
      }
    });
    onClose();
  };

  const handleExportData = () => {
    const data = {
      settings: state.settings,
      user: state.currentUser,
      calculations: state.calculations,
      variables: state.variables,
      chatMessages: state.chatMessages,
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decalculator-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        
        if (data.settings) {
          dispatch({ type: 'UPDATE_APP_SETTINGS', payload: data.settings });
        }
        if (data.user) {
          dispatch({ type: 'SET_CURRENT_USER', payload: data.user });
        }
        if (data.calculations) {
          data.calculations.forEach((calc: any) => {
            dispatch({ type: 'ADD_CALCULATION', payload: { ...calc, timestamp: new Date(calc.timestamp) } });
          });
        }
        if (data.variables) {
          data.variables.forEach((var_: any) => {
            dispatch({ type: 'ADD_VARIABLE', payload: { ...var_, timestamp: new Date(var_.timestamp) } });
          });
        }
        if (data.chatMessages) {
          data.chatMessages.forEach((msg: any) => {
            dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { ...msg, timestamp: new Date(msg.timestamp) } });
          });
        }
        
        alert('Data imported successfully!');
      } catch (error) {
        alert('Failed to import data. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const handleClearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      dispatch({ type: 'CLEAR_ALL' });
      localStorage.clear();
      alert('All data cleared successfully!');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-dark-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-dark-700 flex-shrink-0">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 text-dark-400 hover:text-white transition-colors rounded-lg hover:bg-dark-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-48 sm:w-64 bg-dark-900 border-r border-dark-700 p-2 sm:p-4 flex-shrink-0">
            <nav className="space-y-2">
              {[
                { id: 'general', label: 'General', icon: Palette },
                { id: 'session', label: 'Session', icon: User },
                { id: 'data', label: 'Data & Storage', icon: Save },
                { id: 'profile', label: 'Profile', icon: User },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    activeTab === id
                      ? 'bg-accent-600 text-white'
                      : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 p-3 sm:p-6 overflow-y-auto">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">General Settings</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-white font-medium">Auto Save</label>
                      <p className="text-sm text-dark-400">Automatically save your work</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tempSettings.autoSave}
                        onChange={(e) => setTempSettings({ ...tempSettings, autoSave: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-white font-medium">Sound Effects</label>
                      <p className="text-sm text-dark-400">Play sounds for interactions</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tempSettings.soundEnabled}
                        onChange={(e) => setTempSettings({ ...tempSettings, soundEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-white font-medium">Notifications</label>
                      <p className="text-sm text-dark-400">Show browser notifications</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tempSettings.notifications}
                        onChange={(e) => setTempSettings({ ...tempSettings, notifications: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-white font-medium">Auto Clear on Session End</label>
                      <p className="text-sm text-dark-400">Clear data when leaving sessions</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tempSettings.autoClearOnSessionEnd}
                        onChange={(e) => setTempSettings({ ...tempSettings, autoClearOnSessionEnd: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                    </label>
                  </div>

                  <div>
                    <label className="text-white font-medium">Max History Items</label>
                    <p className="text-sm text-dark-400 mb-2">Maximum number of calculations to keep</p>
                    <input
                      type="number"
                      value={tempSettings.maxHistoryItems}
                      onChange={(e) => setTempSettings({ ...tempSettings, maxHistoryItems: parseInt(e.target.value) || 100 })}
                      className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500"
                      min="10"
                      max="1000"
                    />
                  </div>

                  <div>
                    <label className="text-white font-medium">Max Chat Messages</label>
                    <p className="text-sm text-dark-400 mb-2">Maximum number of chat messages to keep</p>
                    <input
                      type="number"
                      value={tempSettings.maxChatMessages}
                      onChange={(e) => setTempSettings({ ...tempSettings, maxChatMessages: parseInt(e.target.value) || 200 })}
                      className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500"
                      min="10"
                      max="1000"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-white font-medium">Allow Anonymous Users</label>
                      <p className="text-sm text-dark-400">Allow users to join without creating a profile</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tempSettings.allowAnonymousUsers}
                        onChange={(e) => setTempSettings({ ...tempSettings, allowAnonymousUsers: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                    </label>
                  </div>

                  <div>
                    <label className="text-white font-medium">Default Anonymous Name</label>
                    <p className="text-sm text-dark-400 mb-2">Default name for anonymous users</p>
                    <input
                      type="text"
                      value={tempSettings.defaultAnonymousName}
                      onChange={(e) => setTempSettings({ ...tempSettings, defaultAnonymousName: e.target.value })}
                      className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500"
                      placeholder="Anonymous User"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'session' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">Session Settings</h3>
                
                {state.currentSession ? (
                  <div className="space-y-4">
                    <div className="bg-dark-700 rounded-lg p-4">
                      <h4 className="text-white font-medium mb-2">Current Session: {state.currentSession.name}</h4>
                      <p className="text-sm text-dark-400">Created: {state.currentSession.createdAt.toLocaleString()}</p>
                      <p className="text-sm text-dark-400">Users: {state.sessionUsers.length}/{state.currentSession.maxUsers}</p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-white font-medium">Auto Clear on Join</label>
                        <p className="text-sm text-dark-400">Clear data when new users join</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tempSessionSettings.autoClearOnJoin}
                          onChange={(e) => setTempSessionSettings({ ...tempSessionSettings, autoClearOnJoin: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-white font-medium">Auto Clear on Reload</label>
                        <p className="text-sm text-dark-400">Clear data when page reloads</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tempSessionSettings.autoClearOnReload}
                          onChange={(e) => setTempSessionSettings({ ...tempSessionSettings, autoClearOnReload: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-white font-medium">Allow Anonymous Users</label>
                        <p className="text-sm text-dark-400">Allow users without profiles to join</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tempSessionSettings.allowAnonymousUsers}
                          onChange={(e) => setTempSessionSettings({ ...tempSessionSettings, allowAnonymousUsers: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-white font-medium">Require Approval</label>
                        <p className="text-sm text-dark-400">Require approval for calculations</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tempSessionSettings.requireApproval}
                          onChange={(e) => setTempSessionSettings({ ...tempSessionSettings, requireApproval: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <User className="h-12 w-12 mx-auto mb-4 text-dark-500" />
                    <p className="text-dark-400">No active session</p>
                    <p className="text-sm text-dark-500">Join or create a session to configure session settings</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'data' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">Data & Storage</h3>
                
                <div className="space-y-4">
                  <div className="bg-dark-700 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-2">Storage Information</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-dark-400">Calculations:</span>
                        <span className="text-white ml-2">{state.calculations.length}</span>
                      </div>
                      <div>
                        <span className="text-dark-400">Variables:</span>
                        <span className="text-white ml-2">{state.variables.length}</span>
                      </div>
                      <div>
                        <span className="text-dark-400">Chat Messages:</span>
                        <span className="text-white ml-2">{state.chatMessages.length}</span>
                      </div>
                      <div>
                        <span className="text-dark-400">Storage Used:</span>
                        <span className="text-white ml-2">
                          {Math.round((JSON.stringify(localStorage).length / 1024) * 100) / 100} KB
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={handleExportData}
                      className="flex items-center space-x-3 p-4 bg-accent-600 hover:bg-accent-700 text-white rounded-lg transition-all duration-200"
                    >
                      <Download className="h-5 w-5" />
                      <span>Export All Data</span>
                    </button>

                    <label className="flex items-center space-x-3 p-4 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-all duration-200 cursor-pointer">
                      <Upload className="h-5 w-5" />
                      <span>Import Data</span>
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImportData}
                        className="hidden"
                      />
                    </label>

                    <button
                      onClick={() => dispatch({ type: 'CLEAR_HISTORY' })}
                      className="flex items-center space-x-3 p-4 bg-warning-600 hover:bg-warning-700 text-white rounded-lg transition-all duration-200"
                    >
                      <Trash2 className="h-5 w-5" />
                      <span>Clear History</span>
                    </button>

                    <button
                      onClick={() => dispatch({ type: 'CLEAR_CHAT' })}
                      className="flex items-center space-x-3 p-4 bg-warning-600 hover:bg-warning-700 text-white rounded-lg transition-all duration-200"
                    >
                      <Trash2 className="h-5 w-5" />
                      <span>Clear Chat</span>
                    </button>
                  </div>

                  <div className="border-t border-dark-700 pt-4">
                    <button
                      onClick={handleClearAllData}
                      className="w-full flex items-center justify-center space-x-3 p-4 bg-danger-600 hover:bg-danger-700 text-white rounded-lg transition-all duration-200"
                    >
                      <Trash2 className="h-5 w-5" />
                      <span>Clear All Data</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">User Profile</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-white font-medium">Display Name</label>
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500 mt-2"
                    />
                  </div>

                  <div>
                    <label className="text-white font-medium">Avatar</label>
                    <div className="grid grid-cols-6 gap-2 mt-2">
                      {avatars.map((avatar) => (
                        <button
                          key={avatar}
                          onClick={() => setUserAvatar(avatar)}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                            userAvatar === avatar
                              ? 'border-accent-500 bg-accent-900'
                              : 'border-dark-600 hover:border-dark-500'
                          }`}
                        >
                          <span className="text-2xl">{avatar}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-white font-medium">Color</label>
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      {colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setUserColor(color)}
                          className={`w-12 h-12 rounded-lg border-2 transition-all duration-200 ${
                            userColor === color
                              ? 'border-white scale-110'
                              : 'border-dark-600 hover:border-dark-500'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="bg-dark-700 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-2">Preview</h4>
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold border-2 border-dark-600"
                        style={{ backgroundColor: userColor }}
                      >
                        {userAvatar}
                      </div>
                      <div>
                        <p className="text-white font-medium">{userName || 'Your Name'}</p>
                        <p className="text-sm text-dark-400">Online</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-3 sm:p-6 border-t border-dark-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-700 transition-all duration-200 font-medium text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={activeTab === 'profile' ? handleSaveProfile : handleSaveSettings}
            className="px-3 sm:px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-all duration-200 font-medium shadow-md text-sm sm:text-base"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
